import { Component, type ErrorInfo, type ReactNode } from "react";
import { i18n } from "@njbsoft/i18n";
import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // The customer portal reports to no error service, so the boundary catch
    // goes to the console for local diagnosis.
    console.error(error, errorInfo.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="mx-auto max-w-xl px-4 py-12 text-center">
          <h1 className="text-xl font-semibold tracking-[-0.01em] text-ink">
            {i18n.t("errors.somethingWentWrong")}
          </h1>
          <p className="mt-2 text-sm text-muted">{i18n.t("errors.unexpectedError")}</p>
          <Button className="mt-4" onClick={() => this.setState({ hasError: false, error: null })}>
            {i18n.t("errors.tryAgain")}
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
